import { useMutation } from '@apollo/react-hooks';
import {
  Grid,
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect } from 'react';
import { DialogForm } from '../../components/DialogForm';
import { ADD_PARTICIPANT } from '../graphQL/queries';
import AddParticipantFormDataConfig, {
  ParameterTypeConfig,
  PrimaryParamterConfig,
  ClientPrimaryCredsConfig
} from './formDataConfig';
import Delete from '@jda/lui-common-icon-library/ui/Delete';
import PlusCircle from '@jda/lui-common-icon-library/ui/PlusCircle';
import _ from 'lodash';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%'
  },
  dialogTitle: {
    padding: theme.spacing(2),
    borderBottom: '1px solid'
  },
  progress: {
    color: theme.palette.background.paper
  },
  textField: {
    width: '80%'
  },
  grid: {
    width: '100%',
    borderBottom: '1px solid #EBEBEB'
  },
  headers: {
    color: theme.palette.text.secondary
  }
}));

type specObj = {
  participantCode: string;
  companyName: string;
  primaryEmail: string;
  status: string;
  workflowsCount: number;
  providerCount: number;
  customerCount: number;
};

interface AddParticipantDialogProps {
  dialogOpen: true | false;
  handleDialogClose: any;
  handleDialogSubmit: any;
  edit: true | false;
  formData: any;
  refresh: any;
  formInfo: any;
  supportUser?: true | false;
}

let dataConfig = [...AddParticipantFormDataConfig];

const AddParticipantDialog = (props: AddParticipantDialogProps) => {
  const classes = useStyles();

  const { edit, formData } = props;
  const [addParticipantQuery] = useMutation(ADD_PARTICIPANT);

  const [formInfo, setFormInfo] = React.useState<any>({});
  const [showCreds, isShowCreds] = React.useState<any>(false);
  const [clientPrimaryCreds, setClientPrimaryCreds] = React.useState<any>({});
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (props.edit) {
      const editData: any[] = [];
      (dataConfig || []).forEach((obj: any) => {
        let temp = { ...obj };
        if (temp.key === 'participantType' || temp.key === 'participantCode' || temp.key === 'companyName') {
          temp.disabled = true;
        }
        editData.push(temp);
      });
      dataConfig = [...editData];
      const list: any = [];

      if (formData?.additionalCreds) {
        const additionalCredsObj = formData.additionalCreds;
        _.keys(additionalCredsObj || []).forEach(keyParameter => {
          const additionalParameterObj = additionalCredsObj[keyParameter];
          _.keys(additionalParameterObj || []).forEach(key => {
            list.push({ parameterType: keyParameter, key: key, description: additionalParameterObj[key] });
          });
        });
      }

      formData.isAdditionalParametersRequired = list.length > 0 ? 'yes' : 'no';
      setFormInfo({
        isAdditionalParametersRequired: list.length > 0 ? 'yes' : 'no',
        parameters: [...list]
      });
    } else {
      setFormInfo({ ...props.formInfo });
    }
  }, [formData, props.edit, props.formInfo]);

  const handleAddParticipantDialogSubmit = (formDataObj: any) => {
    let additionalCredsObj: any = {};

    let authHeaderObjValues: any = {};
    let authformParamsObjValues: any = {};
    let requestHeadersObjValues: any = {};
    let authBodyParametersObjValues: any = {};
    let primaryParametersObjValues: any = {};

    if (formInfo.parameters) {
      _.forEach(formInfo.parameters || [], (element: any) => {
        if (element.parameterType === 'authHeaders') {
          authHeaderObjValues[element.key] = element.description;
        } else if (element.parameterType === 'formParams') {
          authformParamsObjValues[element.key] = element.description;
        } else if (element.parameterType === 'requestHeaders') {
          requestHeadersObjValues[element.key] = element.description;
        } else if (element.parameterType === 'authBodyParameters') {
          authBodyParametersObjValues[element.key] = element.description;
        } else if (element.parameterType === 'primary') {
          primaryParametersObjValues[element.key] = element.description;
        }
      });
    }
    additionalCredsObj.authHeaders = authHeaderObjValues;
    additionalCredsObj.formParams = authformParamsObjValues;
    additionalCredsObj.requestHeaders = requestHeadersObjValues;
    additionalCredsObj.authBodyParameters = authBodyParametersObjValues;
    additionalCredsObj.primary = primaryParametersObjValues;
    props.handleDialogSubmit();
    addParticipantQuery({
      variables: {
        participantCode: formDataObj.participantCode,
        participantType: formDataObj.participantType,
        companyName: formDataObj.companyName,
        primaryEmail: formDataObj.primaryEmail,
        additionalCreds: additionalCredsObj,
        method: edit ? 'PUT' : 'POST'
      }
    })
      .then(response1 => {
        enqueueSnackbar(edit ? 'Participant Details Modified Successfully !!' : 'Participant Added Successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        setFormInfo({});
        if (!edit) {
          isShowCreds(true);
          setClientPrimaryCreds(response1?.data?.addParticipants?.clientCredentials || {});
        }
        props.refresh();
        return response1;
      })
      .catch(error => {
        enqueueSnackbar('Add Participant action failed !!', { variant: 'error', preventDuplicate: true });
        setFormInfo({});
      });
  };

  const getLatestValues = (dataObj: any) => {
    dataObj.parameters = [];
    setFormInfo({ ...dataObj });
  };

  const handleAddParameter = () => {
    const list = formInfo.parameters;
    list.push({ parameterType: 'authHeaders', key: '', description: '' });
    setFormInfo({
      ...formInfo,
      parameters: list
    });
  };

  const handleParameterChange = (e: any, index: number, label: string) => {
    formInfo.parameters[index][label] = e.target.value;
    if (label === 'parameterType' && e.target.value === 'primary') {
      formInfo.parameters[index]['key'] = 'identifier';
    }
    setFormInfo({
      ...formInfo
    });
  };

  const handleDeleteParameter = (index: number) => {
    formInfo.parameters.splice(index, 1);
    setFormInfo({
      ...formInfo
    });
  };

  const handleCredsDialog = () => {
    isShowCreds(false);
    setClientPrimaryCreds({});
  };

  return (
    <div>
      <DialogForm
        key={edit ? `edit-participant-${formData.participantCode}` : 'new-participant-dialog'}
        openFn={props.dialogOpen}
        closeFn={props.handleDialogClose}
        dataConfig={edit ? dataConfig : [...AddParticipantFormDataConfig]}
        handleDialogClose={props.handleDialogClose}
        handleDialogSubmit={handleAddParticipantDialogSubmit}
        submitlabel="Submit"
        dialogId={edit ? `edit-participant-${formData.participantCode}` : 'new-participant-dialog'}
        title={edit ? 'Edit Participant' : 'New Participant'}
        getLatestValues={getLatestValues}
        data={formData}
        supportUser={props?.supportUser}
      >
        {formInfo.isAdditionalParametersRequired === 'yes' && !props.supportUser && (
          <Box m={1}>
            <Box className={classes.grid}>
              <Grid container alignItems="center">
                <Grid item xs={10}>
                  <Typography variant="subtitle1" style={{ padding: '8px' }}>
                    Additional Identifiers
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <PlusCircle data-id="add-Button" onClick={handleAddParameter} />
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.grid} style={{ padding: '8px' }}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Parameter</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Key</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Description</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box m={2}>
              {(formInfo?.parameters || '').map((paramObj: any, index: number) => (
                <Grid container style={{ width: '100%' }} spacing={3} alignItems="center">
                  <Grid item xs={3} justify="center" alignItems="flex-end">
                    <Select
                      style={{ width: '90%' }}
                      data-id={`parameter-paramType-${index}`}
                      id={`parameter-paramType-${index}`}
                      value={paramObj.parameterType}
                      onChange={e => handleParameterChange(e, index, 'parameterType')}
                    >
                      {ParameterTypeConfig.map((item: any) => [<MenuItem value={item.value}>{item.label}</MenuItem>])}
                    </Select>
                  </Grid>
                  <Grid item xs={4}>
                    {paramObj.parameterType === 'primary' ? (
                      <Select
                        style={{ minWidth: '80%' }}
                        data-id={`parameter-key-${index}`}
                        id={`parameter-key-${index}`}
                        value={paramObj.key}
                        onChange={e => handleParameterChange(e, index, 'key')}
                      >
                        {PrimaryParamterConfig.map((item: any) => [
                          <MenuItem value={item.value}>{item.label}</MenuItem>
                        ])}
                      </Select>
                    ) : (
                      <TextField
                        data-id={`parameter-key-${index}`}
                        id={`parameter-key-${index}`}
                        className={classes.textField}
                        value={paramObj.key || ''}
                        onChange={e => handleParameterChange(e, index, 'key')}
                      ></TextField>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      data-id={`parameter-description-${index}`}
                      id={`parameter-description-${index}`}
                      className={classes.textField}
                      value={paramObj.description || ''}
                      onChange={e => handleParameterChange(e, index, 'description')}
                    ></TextField>
                  </Grid>
                  <Grid item xs={1}>
                    <Delete data-id="delete-parameter" onClick={() => handleDeleteParameter(index)} />
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        )}
      </DialogForm>
      <Dialog
        open={showCreds}
        // onClose={handleClose}
        aria-labelledby="customized-dialog-title"
      >
        <DialogContent>
          <DialogContentText>
            {clientPrimaryCreds ? (
              <div>
                <Typography>Participant is successfully created with</Typography>
                {(ClientPrimaryCredsConfig || []).map((obj: any) => (
                  <Grid container>
                    <Grid item>
                      <Typography>{`${obj.label}-${clientPrimaryCreds[obj.value]}`}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </div>
            ) : (
              <Typography>Client Credentials are not available</Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" autoFocus variant="contained" onClick={handleCredsDialog}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default AddParticipantDialog;
